import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  Divider,
  CircularProgress,
  useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import { useFetchGenieLogQuery } from '../../store/services/chatlog';
import { useSelector } from 'react-redux';
import { selectUserId } from '../../store/features/authSlice';

interface GenieLog {
  question: string;
  answer: string;
  date: string;
}

interface GenieLogDialogProps {
  onQuestionSelect: (question: string) => void;
}

const GenieLogDialog: React.FC<GenieLogDialogProps> = ({ onQuestionSelect }) => {
  const [open, setOpen] = useState(false);
  const theme = useTheme();
  const userId = useSelector(selectUserId);
  const { data: genieLog, isLoading, error } = useFetchGenieLogQuery(userId || '', {
    skip: !open || !userId
  });

  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);

  const handleQuestionClick = (question: string) => {
    onQuestionSelect(question);
    handleClose();
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };

  return (
    <>
      <IconButton
        onClick={handleOpen}
        sx={{
        //   position: 'fixed',
        //   bottom: 12,
        //   left: 48,
          backgroundColor: theme.palette.primary.main,
          color: 'white',
          '&:hover': {
            backgroundColor: theme.palette.primary.dark,
          },
          marginTop: 2,
          width: 32,
          height: 32,
          boxShadow: 3,
        }}
      >
        <HistoryIcon />
      </IconButton>

      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            position: 'fixed',
            bottom: 0,
            m: 0,
            height: '60vh',
            maxHeight: '60vh',
          }
        }}
      >
        <DialogTitle sx={{ 
          m: 0, 
          p: 2, 
          display: 'flex', 
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: `1px solid ${theme.palette.divider}`
        }}>
          {/* <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}> */}
            Genie Conversation History
          {/* </Typography> */}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              color: theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent sx={{ p: 0 }}>
          {isLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          ) : error ? (
            <Box sx={{ p: 2 }}>
              <Typography color="error">Error loading conversation history</Typography>
            </Box>
          ) : genieLog && genieLog.length > 0 ? (
            <List sx={{ width: '100%', bgcolor: 'background.paper' }}>
              {genieLog.map((log: GenieLog, index: number) => (
                <React.Fragment key={index}>
                  <ListItem
                    component="div"
                    alignItems="flex-start" 
                    sx={{ 
                      flexDirection: 'column',
                      cursor: 'pointer',
                      '&:hover': {
                        backgroundColor: theme.palette.action.hover,
                      }
                    }}
                    onClick={() => handleQuestionClick(log.question)}
                  >
                    <ListItemText
                      primary={
                        <Typography variant="subtitle1" color="primary">
                          {log.question}
                        </Typography>
                      }
                      secondary={
                        <Typography
                          component="span"
                          variant="body2"
                          sx={{
                            display: 'block',
                            color: 'text.secondary',
                            mt: 1
                          }}
                        >
                          {log.date}
                          <Typography
                            component="span"
                            variant="body2"
                            sx={{
                              display: 'block',
                              mt: 1
                            }}
                          >
                            {log.answer}
                          </Typography>
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < genieLog.length - 1 && <Divider component="li" />}
                </React.Fragment>
              ))}
            </List>
          ) : (
            <Box sx={{ p: 2 }}>
              <Typography>No conversation history available</Typography>
            </Box>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};

export default GenieLogDialog; 